import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiBox, FiFileText, FiLogOut, FiPhone, FiSettings, FiUser } from "react-icons/fi";

import styles from "./Layout.module.scss";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useAuth } from "../../context/AuthContext";
import { fetchLoggingUserData, getLoggingUser, setPermissionsFromLogin } from "../../features/loggingUser/loggingUserSlice";
import ImgModal from "../modals/ImgModal";
import BranchModal from "../branch/BranchModal";
import BranchSelector from "../branch/BranchSelector";
import { t } from "../../locales";
import { route } from "../../static/route";
import { getBranches, getBranchesLoaded, getIsImgModalOpen, getSelectedBranch, setBranchesFromResponse } from "../../features/settings/settingsSlice";
import { POST } from "../../services/axios.service";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { useCanAccess } from "../../hooks/useCanAccess";

type Props = {
  children: React.ReactNode;
  className?: string;
};

const Layout = (props: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setIsAuthenticated } = useAuth();
  const authToken = localStorage.getItem("authToken");
  const isImgModalOpen = useAppSelector(getIsImgModalOpen);
  const loggingUser = useAppSelector(getLoggingUser);
  const branches = useAppSelector(getBranches);
  const branchesLoaded = useAppSelector(getBranchesLoaded);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const canAccess = useCanAccess();
  const showBranchSelector = branches.length > 1;
  const isBranchReady = !branchesLoaded ? false : (branches.length <= 1 || selectedBranch !== null);

  const companyName = process.env.REACT_APP_COMPANY_NAME || "Hardware ERP";
  const feedbackWidgetEnabled = process.env.REACT_APP_FEEDBACK_WIDGET_ENABLED === "true";
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (authToken) {
      dispatch(fetchLoggingUserData()).then((action: any) => {
        if (fetchLoggingUserData.fulfilled.match(action)) {
          if (action.payload?.data?.status === "success") {
            const data = action.payload.data.data;
            dispatch(setBranchesFromResponse(data.branches ?? []));
            dispatch(setPermissionsFromLogin({
              permissions: data.permissions ?? {},
              roles: data.roles ?? {},
            }));
          } else {
            localStorage.removeItem("authToken");
            localStorage.removeItem("selectedBranch");
            setIsAuthenticated(false);
            navigate("/login");
          }
        }
        // If rejected (401 already handled by axios interceptor, or transient error),
        // don't force logout — let the user retry or the interceptor handle it.
      });
    }
  }, [authToken]);

  useEffect(() => {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }, []);

  const handleLogout = async () => {
    try {
      const response: any = await POST("/v1/logout", {});
      if (response.data.status === "success") {
        localStorage.removeItem("authToken");
        localStorage.removeItem("selectedBranch");
        setIsAuthenticated(false);
        navigate("/");
      }
    } catch (_) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("selectedBranch");
      setIsAuthenticated(false);
      navigate("/");
    }
  };

  const initials = loggingUser?.name
    ? loggingUser.name
        .split(" ")
        .map((w: string) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "U";

  return (
    <div className={`${styles.Layout} ${props.className ?? ""}`}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow flex items-center justify-between px-4 md:px-6 h-14 md:h-16">
        <Link to="/dashboard" className="flex items-center gap-2 text-brand-dark font-semibold text-lg">
          <FiBox className="text-2xl" />
          <span>{companyName}</span>
        </Link>

        <div className="flex items-center gap-5">
          <div className="hidden sm:block">
            <BranchSelector />
          </div>

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button className="focus:outline-none p-1 rounded-full border-2 border-[#34699A] hover:bg-gray-100 transition-colors text-[#34699A] hover:text-[#113F67]">
                <FiUser className="text-xl" />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-64">
              {/* Mobile-only branch selector */}
              {showBranchSelector && (
                <div className="sm:hidden px-3 py-2 border-b border-gray-100">
                  <p className="text-xs font-medium text-gray-500 mb-1.5">{t.layout.selectBranchLabel}</p>
                  <BranchSelector />
                </div>
              )}

              {/* User info */}
              <DropdownMenuLabel className="flex items-center gap-3 px-3 py-3">
                <Avatar className="h-10 w-10 flex-shrink-0">
                  <AvatarFallback className="bg-[#113F67] text-white font-semibold text-sm">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800 text-sm truncate">
                    {loggingUser?.name || "—"}
                  </p>
                  {loggingUser?.phone && (
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 font-normal">
                      <FiPhone className="flex-shrink-0" />
                      {loggingUser.phone}
                    </p>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={() => navigate(route.profile)} className="text-gray-700 focus:bg-gray-50">
                <FiUser className="mr-2 flex-shrink-0" />
                <span>{t.layout.manageProfile}</span>
              </DropdownMenuItem>

              {canAccess("settings") && (
                <DropdownMenuItem onClick={() => navigate(route.settings)} className="text-gray-700 focus:bg-gray-50">
                  <FiSettings className="mr-2 flex-shrink-0" />
                  <span>{t.layout.settings}</span>
                </DropdownMenuItem>
              )}

              {canAccess("activity-logs") && (
                <DropdownMenuItem onClick={() => navigate(route.activityLogs)} className="text-gray-700 focus:bg-gray-50">
                  <FiFileText className="mr-2 flex-shrink-0" />
                  <span>{t.layout.activityLogs}</span>
                </DropdownMenuItem>
              )}

              {feedbackWidgetEnabled && canAccess("dev-feedback") && (
                <DropdownMenuItem onClick={() => navigate(route.devFeedback)} className="text-gray-700 focus:bg-gray-50">
                  <FiFileText className="mr-2 flex-shrink-0" />
                  <span>{t.layout.devFeedback}</span>
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 focus:bg-red-50 focus:text-red-600"
              >
                <FiLogOut className="mr-2 flex-shrink-0" />
                <span>{t.layout.logout}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main content */}
      <div className={`mainContent flex-1 mt-14 md:mt-16 ${isImgModalOpen ? "bg-primary-2 opacity-20" : ""}`}>
        {isBranchReady ? (
          props.children
        ) : (
          <div className="flex items-center justify-center h-[60vh]">
            <span className="w-8 h-8 rounded-full border-4 border-gray-200 border-t-[#113F67] animate-spin" />
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-4 text-center text-sm text-white" style={{ backgroundColor: "#113F67" }}>
        &copy; {currentYear} {companyName}. All Rights Reserved.
      </footer>

      <ImgModal />
      <BranchModal />
    </div>
  );
};

export default Layout;
